import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import page from '../LabelPrintingPage.module.css'
import styles from './Labels.module.css'
import { ApiError, apiJson, inCurrentWorkspace } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { Failed, PendingButton, SkeletonTableRows, inertClass, useSlow } from '../../components/pending'
import { useTasks, type TaskFile } from '../../components/TaskCentre'
import { labelAccess, notAllowed, plural, type LabelAccess, type Purpose } from './model'
import { WATCH_MS, renderTask } from './render'

// Print history (§7m.6, h 4722-4744). The list searches batch ID, Display ID,
// person, group, job and creator and filters by status; a batch opens to its
// detail: each label as issued, what it links to now, and the append-only
// audit trail. The first download issues a batch and from then on it can
// never be voided; a reprint returns the exact retained file with a reason
// that is recorded; only a Ready batch nobody has downloaded can be voided.

export type BatchStatus = 'Draft' | 'Generating' | 'Ready' | 'Downloaded' | 'Reprinted' | 'Voided' | 'Failed'

export interface LabelBatch {
  id: string
  purpose: Purpose
  status: BatchStatus
  format_id: string
  format_name: string | null
  format_version: number | null
  colour: string
  quantity: number
  assigned: boolean
  display_id_mode: string | null
  people_count: number
  picking_job_id: string | null
  job_name: string | null
  created_at: string
  created_by: string
  created_by_email: string | null
  generated_at: string | null
  first_download_at: string | null
  first_download_by_email: string | null
  voided_at: string | null
  voided_by_email: string | null
  void_reason: string | null
  label_count: number
  planned_count: number
  reprint_count: number
  file_is_pdf: boolean
}

interface BatchLabel {
  id: string
  sheet_position: number | null
  display_id: string
  prefix: string
  purpose: Purpose
  printed_display_id: string | null
  printed_worker_name: string | null
  printed_group_name: string | null
  printed_job_name: string | null
  printed_variety: string | null
  worker_name_now: string | null
  job_name_now: string | null
}

interface TrailEvent {
  event: string
  timestamp?: string
  user?: string
  user_email?: string | null
  [k: string]: unknown
}

interface BatchDetailView extends LabelBatch {
  items: BatchLabel[]
  items_shown: number
  audit_trail: TrailEvent[]
}

interface RetainedFile {
  name: string
  content_type: string
  base64: string
}

// V4's chips. The route also takes Draft and Generating, which a batch passes
// through on its way to Ready and does not stay in.
const FILTERS: ('All' | BatchStatus)[] = ['All', 'Ready', 'Downloaded', 'Reprinted', 'Failed', 'Voided']

const PILL: Record<BatchStatus, string> = {
  Draft: styles.pillMuted,
  Generating: styles.pillMuted,
  Ready: styles.pillInfo,
  Downloaded: styles.pillGood,
  Reprinted: styles.pillGood,
  Voided: styles.pillMuted,
  Failed: styles.pillBad,
}

export const shortId = (id: string) => id.slice(0, 8)
const purposeName = (p: Purpose) => (p === 'worker' ? 'Worker ID' : 'Item tracking')
const formatName = (b: LabelBatch) => (b.format_name ? `${b.format_name} · template v${b.format_version}` : 'Format no longer listed')
const labelCount = (b: LabelBatch) => b.label_count || b.planned_count
const who = (email: string | null | undefined, uid?: string | null) => email || uid || 'someone'
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')
const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

function StatusPill({ status }: { status: BatchStatus }) {
  return <span className={[styles.pill, PILL[status] || styles.pillMuted].join(' ')}>{status}</span>
}

// The retained file, as the task centre hands it over (§6d): a file is never
// a direct click-to-download, so the bytes land on a task and the rail's
// Download is where the browser gets them.
function taskFile(file: RetainedFile): TaskFile {
  const bin = atob(file.base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return { name: file.name, content: bytes, type: file.content_type }
}

export function PrintHistory({
  user,
  toast,
  access = labelAccess(null),
  onAccessChanged,
}: {
  user: any
  toast: (message: string) => void
  access?: LabelAccess
  onAccessChanged?: () => void
}) {
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'All' | BatchStatus>('All')
  const [batches, setBatches] = useState<LabelBatch[]>([])
  const [shown, setShown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  // Moved on by every change a detail makes, so the list reads again.
  const [tick, setTick] = useState(0)
  const seq = useRef(0)

  // The search is sent once typing pauses, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setSearch(typed.trim()), 300)
    return () => clearTimeout(t)
  }, [typed])

  useEffect(() => {
    const n = ++seq.current
    const q = new URLSearchParams()
    if (status !== 'All') q.set('status', status)
    if (search) q.set('search', search)
    const qs = q.toString()
    apiJson<LabelBatch[]>(user, `/api/label-printing/batches${qs ? `?${qs}` : ''}`)
      .then((rows) => {
        if (n !== seq.current) return
        setBatches(Array.isArray(rows) ? rows : [])
        setError(null)
      })
      .catch((err) => n === seq.current && setError(err instanceof Error ? err.message : 'Could not load the print history'))
      .finally(() => n === seq.current && setShown(true))
  }, [user, status, search, tick])

  // A batch rendering in the background moves on by itself, so the list
  // reads again while one is shown.
  const rendering = batches.some((b) => b.status === 'Generating')
  useEffect(() => {
    if (!rendering) return
    const t = setTimeout(() => setTick((x) => x + 1), WATCH_MS * 2)
    return () => clearTimeout(t)
  }, [rendering, tick])

  const filtered = status !== 'All' || !!search

  return (
    <div className={page.historySection}>
      <div className={styles.historyTools}>
        <input
          type="search"
          className={styles.pickerSearch}
          aria-label="Search print history"
          placeholder="Batch ID, Display ID, person, group, job or creator"
          value={typed}
          maxLength={100}
          onChange={(e) => setTyped(e.target.value)}
        />
        <div className={styles.seg} role="radiogroup" aria-label="Status">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={status === f}
              className={[styles.segBtn, status === f ? styles.segOn : ''].join(' ')}
              onClick={() => setStatus(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className={page.errorBanner} role="alert">
          <p>{error}</p>
        </div>
      )}

      {shown && !error && batches.length === 0 ? (
        <div className={page.emptyState}>
          <p>{filtered ? 'No batch matches this search and filter.' : 'No label batches yet. Create one to get started.'}</p>
        </div>
      ) : (
        <div className={page.batchTable}>
          <table>
            <thead>
              <tr>
                <th>Batch ID</th>
                <th>Purpose</th>
                <th>Format</th>
                <th>Labels</th>
                <th>Picking job</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            {!shown ? (
              <SkeletonTableRows cols={7} label="Loading print history" />
            ) : (
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} data-testid={`batch-${shortId(b.id)}`}>
                    <td>
                      <button type="button" className={page.actionLink} aria-label={`Open batch ${shortId(b.id)}`} onClick={() => setOpenId(b.id)}>
                        {shortId(b.id)}
                      </button>
                    </td>
                    <td>{purposeName(b.purpose)}</td>
                    <td>{formatName(b)}</td>
                    <td>{labelCount(b)}</td>
                    <td>{b.job_name || '—'}</td>
                    <td>
                      <StatusPill status={b.status} />
                    </td>
                    <td>
                      {day(b.created_at)} · {who(b.created_by_email, b.created_by)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}

      {openId && <BatchDetail user={user} id={openId} access={access} onAccessChanged={onAccessChanged} onClose={() => setOpenId(null)} onChanged={() => setTick((t) => t + 1)} toast={toast} />}
    </div>
  )
}

// One trail event in words (h 4739-4741). Older batches carry the single
// 'generated_and_ready' event from before commit and render were recorded apart.
function eventLine(e: TrailEvent, purpose: Purpose): { title: string; body?: string } {
  switch (e.event) {
    case 'created':
      return { title: 'Batch created' }
    case 'committed': {
      const n = Number(e.labels) || 0
      const body = purpose === 'worker' ? `${plural(n, 'Worker ID label')}. No new identities: each label is its person.` : plural(n, 'item identity', 'item identities')
      const large = 'Above the large-batch threshold: confirmed first, and rendered in the background.'
      return { title: 'Identities committed', body: e.large ? `${body}${body.endsWith('.') ? '' : '.'} ${large}` : body }
    }
    case 'rendered':
      return { title: 'Rendered', body: e.file === 'placeholder' ? 'A placeholder file, not a PDF: label rendering is still to come.' : undefined }
    case 'render_failed':
      return { title: 'Render failed', body: typeof e.error === 'string' ? e.error : undefined }
    case 'render_retried': {
      const n = Number(e.labels) || 0
      return { title: 'Render retried', body: `The same ${plural(n, 'identity', 'identities')}; nothing new allocated.` }
    }
    case 'generated_and_ready':
      return { title: 'Generated and ready', body: 'Recorded as one event, before commit and render were recorded separately.' }
    case 'downloaded':
      return { title: 'First download', body: 'The batch was issued. It can no longer be voided.' }
    case 'reprinted':
      return { title: 'Reprinted', body: typeof e.reason === 'string' ? `Reason: ${e.reason}` : undefined }
    case 'voided':
      return { title: 'Voided', body: typeof e.reason === 'string' ? `Reason: ${e.reason}` : undefined }
    case 'void_refused':
      return { title: 'Void refused', body: typeof e.refusal === 'string' ? e.refusal : undefined }
    default:
      return { title: e.event }
  }
}

function printedText(l: BatchLabel) {
  const parts = l.purpose === 'worker' ? [l.printed_worker_name, l.printed_group_name] : [l.printed_display_id, l.printed_worker_name, l.printed_group_name, l.printed_job_name, l.printed_variety]
  return parts.filter(Boolean).join(' · ') || '—'
}

function linksTo(l: BatchLabel) {
  if (l.purpose === 'worker') return l.worker_name_now || 'Person no longer on file'
  const parts = [l.worker_name_now || 'No worker', l.job_name_now].filter(Boolean)
  return parts.join(' · ')
}

function footNote(b: LabelBatch) {
  if (b.status === 'Ready') return 'Never downloaded, so this batch can still be voided. The first download issues it for good.'
  if (b.status === 'Voided') return 'Voided before issue. Its Display IDs stay reserved and are never reused.'
  if (b.status === 'Failed') return 'Identities are committed but no file exists yet. Retry renders the same identities.'
  if (b.status === 'Generating') return 'Identities committed and rendering. Nothing is downloadable until the file and its hash are stored.'
  if (b.status === 'Draft') return 'Not generated yet, so there is nothing to download.'
  return `Issued on ${day(b.first_download_at)}. Reprints return this exact file and are recorded with a reason.`
}

// The actions a batch offers are the ones its status allows and the caller's
// permission set grants (§7m.11); one withheld by the set is named, not hidden
// without a word. The server checks each one again when it commits.
const refusedBySet = (err: unknown) => err instanceof ApiError && (err.body?.code === 'PERMISSION_CHANGED' || err.body?.code === 'NOT_PERMITTED')

function withheld(b: LabelBatch, access: LabelAccess, issued: boolean) {
  const out: string[] = []
  if (b.status === 'Ready' && !access.download) out.push('download a batch')
  if (b.status === 'Ready' && !access.void) out.push('void a batch')
  if (issued && !access.reprint(b.purpose)) out.push(b.purpose === 'worker' ? 'reprint a batch' : 'reprint an item batch')
  if (b.status === 'Failed' && !access.generate(b.purpose)) out.push(`retry the render of ${b.purpose === 'worker' ? 'Worker ID' : 'item tracking'} labels`)
  return out
}

function BatchDetail({
  user,
  id,
  access,
  onClose,
  onChanged,
  toast,
  onAccessChanged,
}: {
  user: any
  id: string
  access: LabelAccess
  onClose: () => void
  onChanged: () => void
  toast: (m: string) => void
  onAccessChanged?: () => void
}) {
  // Refused because the set changed since the page loaded: the page reads the
  // set again, so what it offers matches what the server now allows.
  const refused = (err: unknown) => {
    if (refusedBySet(err)) onAccessChanged?.()
  }
  const [batch, setBatch] = useState<BatchDetailView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState<null | 'reprint' | 'void'>(null)
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: panelEl, modal: true, busy })
  const slow = useSlow(busy)
  const { startTask } = useTasks()

  const load = () =>
    apiJson<BatchDetailView>(user, `/api/label-printing/batches/${id}`)
      .then((b) => {
        setBatch(b)
        setLoadError(null)
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Could not load this batch'))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Every action re-reads the batch, so the trail shows what was recorded,
  // refused attempts included, and the list behind moves with it.
  const after = () => {
    onChanged()
    return load()
  }

  // A batch still rendering is read again until it is not.
  const rendering = batch?.status === 'Generating'
  useEffect(() => {
    if (!rendering) return
    const t = setTimeout(after, WATCH_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendering, batch])

  const b = batch
  const id8 = shortId(id)
  const noun = b?.file_is_pdf ? 'PDF' : 'file'

  // The first download issues the batch. It runs as a task: the POST that
  // issues it is sent when the task runs, and the file lands on the task.
  const download = () => {
    if (!batch || busy) return
    setBusy(true)
    setError(null)
    startTask({
      label: `Labels ${id8}`,
      note: `${plural(total, 'label')} · the first download issues the batch`,
      producesFile: true,
      run: async () => {
        try {
          const r = await apiJson<{ file: RetainedFile }>(user, `/api/label-printing/batches/${id}/download`, { method: 'POST' })
          return { file: taskFile(r.file), note: `${id8} issued. It can no longer be voided.` }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not download this batch')
          setBusy(false)
          refused(err)
          after()
          throw err
        }
      },
      onDone: () => {
        setBusy(false)
        after()
      },
    })
  }

  // Retry renders the identities the batch already holds (h 4705-4706); a
  // large one goes back to the background.
  const retry = async () => {
    if (!batch || busy) return
    setBusy(true)
    setError(null)
    const here = inCurrentWorkspace(user)
    try {
      const r = await apiJson<LabelBatch>(here, `/api/label-printing/batches/${id}/retry`, { method: 'POST' })
      if (r.status === 'Generating') startTask(renderTask(here, id, labelCount(r), noun))
      else if (r.status === 'Ready') toast(`${id8} rendered. The same identities, nothing new allocated.`)
      else setError('The render failed again. The identities are still held; retry when the cause is fixed.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not retry this batch')
      refused(err)
    } finally {
      setBusy(false)
      after()
    }
  }
  const issued = b?.status === 'Downloaded' || b?.status === 'Reprinted'
  const total = b ? labelCount(b) : 0
  const refusals = b ? withheld(b, access, issued) : []

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={panelEl} className={[o.modal, o.wide, styles.detail].join(' ')} role="dialog" aria-modal="true" aria-label={`Batch ${id8}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{id8}</h2>
            {b && (
              <>
                <p className={o.sub}>
                  {[purposeName(b.purpose), b.purpose === 'worker' ? 'DU' : b.assigned ? 'DTU' : 'DT', plural(total, 'label'), b.purpose === 'worker' ? plural(b.people_count, 'person', 'people') : b.assigned ? 'assigned to workers' : 'unassigned'].join(' · ')}
                </p>
                <div style={{ marginTop: '0.5625rem' }}>
                  <StatusPill status={b.status} />
                </div>
              </>
            )}
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={[o.body, inertClass(busy, slow)].join(' ')}>
          {!b && !loadError && <div style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>Loading the batch…</div>}
          {loadError && <Failed text={loadError} />}
          {b && (
            <>
              <div className={styles.facts}>
                <Fact k="Format and version" v={formatName(b)} />
                <Fact k="Display ID mode" v={b.purpose === 'worker' ? 'System UserId' : b.display_id_mode || '—'} />
                <Fact k="Colour" v={b.colour} />
                <Fact k="Created" v={`${day(b.created_at)} · ${who(b.created_by_email, b.created_by)}`} />
                <Fact k="First issued" v={b.first_download_at ? `${when(b.first_download_at)} · ${who(b.first_download_by_email)}` : 'Not yet'} />
                <Fact k="Reprints" v={String(b.reprint_count)} />
              </div>
              {b.job_name && <div style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>Picking job: {b.job_name}</div>}
              {!b.file_is_pdf && (b.status !== 'Draft' && b.status !== 'Generating' && b.status !== 'Failed') && (
                <div className={o.note}><div className={o.noteLine}><span style={{ fontSize: '0.7813rem', lineHeight: 1.5, color: 'var(--dp-ink2)' }}>The retained file is a placeholder, not a PDF: label rendering is still to come. It is issued, reprinted and audited exactly as the PDF will be.</span></div></div>
              )}
              {refusals.length > 0 && (
                <div className={o.note} role="note"><div className={o.noteLine}><span style={{ fontSize: '0.7813rem', lineHeight: 1.5, color: 'var(--dp-ink2)' }}>{notAllowed(access.role, refusals.join(' or '))}</span></div></div>
              )}

              <section style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingBottom: '0.875rem', borderBottom: '1px solid var(--dp-line-soft)' }} aria-label="Labels in this batch">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className={o.label}>Labels in this batch</span>
                </div>
                {b.items.length === 0 ? (
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>No labels have been committed for this batch.</div>
                ) : (
                  <div className={styles.labelTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>Sheet pos</th>
                          <th>Identity</th>
                          <th>Printed text as issued</th>
                          <th>Links to now</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.items.map((l) => (
                          <tr key={l.id}>
                            <td>{l.sheet_position ?? '—'}</td>
                            <td className={styles.mono}>{l.purpose === 'worker' ? `DU · ${l.printed_worker_name || 'person'}` : l.display_id}</td>
                            <td>{printedText(l)}</td>
                            <td>{linksTo(l)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>
                  {b.items_shown < total ? `Showing ${b.items_shown} of ${total} labels.` : `Showing all ${plural(total, 'label')}.`} Truncation warnings come from
                  fitting the text onto the label, which runs with PDF rendering; none can be measured yet.
                </div>
              </section>

              <section style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingBottom: 0 }} aria-label="Audit trail">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className={o.label}>Audit trail</span>
                </div>
                <ol className={styles.trail}>
                  {b.audit_trail.map((e, i) => {
                    const line = eventLine(e, b.purpose)
                    return (
                      <li key={i} className={styles.trailEvent}>
                        <div className={styles.trailHead}>
                          <span className={styles.trailTitle}>{line.title}</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>
                            {when(e.timestamp)} · {who(e.user_email, e.user)}
                          </span>
                        </div>
                        {line.body && <div className={styles.trailBody}>{line.body}</div>}
                      </li>
                    )
                  })}
                </ol>
              </section>
            </>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Close
          </button>
          {b?.status === 'Ready' && access.void && (
            <button type="button" className={styles.dangerGhost} disabled={busy} onClick={() => setAsking('void')}>
              Void batch
            </button>
          )}
          {b && footNote(b) && <span className={styles.footInfo}>{footNote(b)}</span>}
          <Failed text={error} />
          {b?.status === 'Ready' && access.download && (
            <PendingButton className={o.primary} busy={busy} pending="Preparing…" onClick={download}>
              Download and issue
            </PendingButton>
          )}
          {b && issued && access.reprint(b.purpose) && (
            <button type="button" className={o.primary} disabled={busy} onClick={() => setAsking('reprint')}>
              Reprint with a reason
            </button>
          )}
          {b?.status === 'Failed' && access.generate(b.purpose) && (
            <PendingButton className={o.primary} busy={busy} pending="Retrying…" onClick={retry}>
              Retry render
            </PendingButton>
          )}
        </div>
      </div>
      {b && asking === 'reprint' && (
        <ReprintDialog
          user={user}
          batch={b}
          noun={noun}
          onRefused={refused}
          onClose={() => setAsking(null)}
          onDone={() => {
            setAsking(null)
            after()
          }}
        />
      )}
      {b && asking === 'void' && (
        <VoidDialog
          user={user}
          batch={b}
          onClose={() => setAsking(null)}
          onRefused={(err) => {
            refused(err)
            after()
          }}
          onDone={() => {
            setAsking(null)
            toast(`${id8} voided. Nothing was ever downloadable from it.`)
            after()
          }}
        />
      )}
      {confirm}
    </div>
  )
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div className={styles.fact}>
      <span style={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, color: 'var(--dp-muted)', maxWidth: '13.125rem' }}>{k}</span>
      <span className={styles.factValue}>{v}</span>
    </div>
  )
}

// The reprint (h 4731-4734): the exact retained file, never a re-render, with a
// reason that is recorded. For item labels it says the real risk.
function ReprintDialog({
  user,
  batch,
  noun,
  onClose,
  onDone,
  onRefused,
}: {
  user: any
  batch: LabelBatch
  noun: string
  onClose: () => void
  onDone: () => void
  onRefused: (err: unknown) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const el = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el, modal: true, focusFirst: true, dirty: !!reason.trim(), busy })
  const id8 = shortId(batch.id)
  const { startTask } = useTasks()

  // The reprint runs as a task, so the file lands in the task centre (§6d).
  // The dialog stays open until the server has answered: a refusal is shown
  // here, where the reason was typed.
  const send = () => {
    if (busy) return
    if (!reason.trim()) return setError('A reason is required before a reprint.')
    setBusy(true)
    setError(null)
    startTask({
      label: `Reprint ${id8}`,
      note: `The retained ${noun}, returned unchanged`,
      producesFile: true,
      run: async () => {
        try {
          const r = await apiJson<{ file: RetainedFile }>(user, `/api/label-printing/batches/${batch.id}/reprint`, { body: { reason: reason.trim() } })
          return { file: taskFile(r.file), note: `The retained ${noun} was returned again, unchanged. The reason is on the trail.` }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not reprint this batch')
          setBusy(false)
          onRefused(err)
          throw err
        }
      },
      onDone: () => onDone(),
    })
  }

  return (
    <div
      className={o.scrim}
      onClick={(e) => {
        e.stopPropagation()
        requestClose()
      }}
    >
      <div ref={el} className={[o.modal, o.narrow].join(' ')} role="dialog" aria-modal="true" aria-label={`Reprint ${id8}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Reprint {id8}</h2>
            <p className={o.sub}>The exact retained {noun} comes back, never re-rendered against today's names, groups, jobs or template.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={batch.purpose === 'worker' ? o.note : o.warn} role="note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v18M5 8h14" />
            </svg>
            <div style={{ fontSize: '0.7813rem', lineHeight: 1.5 }}>
              {batch.purpose === 'worker'
                ? 'Worker labels keep identifying the same person, so a reprint is safe: but it is still recorded.'
                : 'These are unique item identities. If the original labels are already on buckets, reprinting puts the same identity on two physical objects.'}
            </div>
          </div>
          <div className={o.field}>
            <label className={o.fieldLabel}>Reason (recorded) <span className={o.req}>required</span></label>
            <textarea
              className={o.input}
              aria-label="Reason (recorded)"
              value={reason}
              maxLength={500}
              rows={3}
              placeholder="e.g. sheet jammed in the printer, none usable"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <Failed text={error} />
          <PendingButton className={o.primary} busy={busy} pending="Reprinting…" disabled={!reason.trim()} onClick={send}>
            Reprint the retained {noun}
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}

// The void (h 4729-4731): only a Ready batch nobody has downloaded. A refusal
// is recorded on the trail, so the detail reads again either way.
function VoidDialog({ user, batch, onClose, onDone, onRefused }: { user: any; batch: LabelBatch; onClose: () => void; onDone: () => void; onRefused: (err: unknown) => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const el = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el, modal: true, busy })
  const id8 = shortId(batch.id)

  const send = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await apiJson(user, `/api/label-printing/batches/${batch.id}/void`, { body: {} })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not void this batch')
      setBusy(false)
      // A void the set refuses is on the trail as well (h 4740).
      if ((err instanceof ApiError && err.body?.code === 'VOID_REFUSED') || refusedBySet(err)) onRefused(err)
    }
  }

  return (
    <div
      className={o.scrim}
      onClick={(e) => {
        e.stopPropagation()
        requestClose()
      }}
    >
      <div ref={el} className={[o.modal, o.narrow].join(' ')} role="alertdialog" aria-modal="true" aria-label={`Void ${id8}?`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Void {id8}?</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <p style={{ margin: 0, fontSize: '0.8438rem', lineHeight: 1.6, color: 'var(--dp-ink2)' }}>
            This batch has never been downloaded, so it can still be voided. Its items read as voided, and its Display IDs and encoded values stay
            reserved forever. They are never handed to another item.
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.5, color: 'var(--dp-muted)', marginTop: '0.75rem' }}>After the first download a batch can no longer be voided, because the labels may already be on buckets.</p>
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Keep it
          </button>
          <Failed text={error} />
          <PendingButton className={o.dangerBtn} busy={busy} pending="Voiding…" onClick={send}>
            Void batch
          </PendingButton>
        </div>
      </div>
      {confirm}
    </div>
  )
}
